
import { translate } from "./hooks/translate";
import { hasPageAccess, isNotificationAdmin } from "./config/roles";
import {
  FiServer,
  FiActivity,
  FiHome,
  FiLayers,
  FiUsers,
  FiShoppingCart,
  FiShoppingBag,
  FiUploadCloud,
  FiGlobe,
  FiPlus,
  FiUserPlus,
  FiUserCheck,
  FiInbox,
  FiCpu,
  FiPackage,
  FiMonitor,
  FiFileText,
  FiTool,
  FiTrello,
  FiGitPullRequest,
  FiAlertCircle,
  FiShield,
  FiBarChart2,
  FiZap,
  FiHardDrive,
  FiClock,
  FiPieChart,
  FiList,
  FiStopCircle,
  FiCheckSquare,
  FiTrendingUp,
  FiTrash2,
  FiDatabase,
  FiCalendar,
  FiBell,
} from "react-icons/fi";

// Filter menu items by user profile (user.pages). Admin sees all; others see only pages they have access to.
const filterMenuItemsByPageAccess = (items, user) => {
  if (!user) return [];
  const userRole = user.role?.toLowerCase();
  const userPages = user.pages || [];

  const dedupeByUrl = (arr) => {
    const seen = new Set();
    return (arr || []).filter((item) => {
      // Parent/group items may not have url; never de-dupe those.
      if (!item?.url) return true;
      if (seen.has(item.url)) return false;
      seen.add(item.url);
      return true;
    });
  };

  // Admin-only items (system companies, logs, licenses) - never show to non-admin
  const adminOnlyUrls = ["/system/companies", "/system/licenses", "/logs"];
  const isAdminOnly = (url) => adminOnlyUrls.includes(url);

  // Admin or Tam Erişim profile — profile-only pages (e.g. EPC) need explicit grant for admin only
  if (userRole === "admin" || user.fullAccess) {
    const applyProfileOnlyFilter = (arr) =>
      dedupeByUrl(arr || []).filter((item) => {
        if (!item?.url) return true;
        return hasPageAccess(
          userRole,
          [],
          item.url,
          userPages,
          !!user.fullAccess,
        );
      });

    if (userRole !== "admin") {
      // For non-system-admin with full access, filter out the admin-only pages
      const filterAdminOnlyTree = (item) => {
        if (!item || item.adminOnly || (item.url && isAdminOnly(item.url)))
          return null;
        const filteredChildren = Array.isArray(item.items)
          ? applyProfileOnlyFilter(
            item.items.map(filterAdminOnlyTree).filter(Boolean),
          )
          : null;
        if (
          item.url &&
          !hasPageAccess(userRole, [], item.url, userPages, !!user.fullAccess)
        ) {
          return filteredChildren?.length
            ? { ...item, url: undefined, items: filteredChildren }
            : null;
        }
        return { ...item, items: filteredChildren || undefined };
      };
      return applyProfileOnlyFilter(
        (items || []).map(filterAdminOnlyTree).filter(Boolean),
      );
    }
    return applyProfileOnlyFilter(items);
  }

  // Employee/User: filter by user.pages (profile-based access)
  // Supports nested sub-menus at any depth via item.items.
  const filterNavTree = (item) => {
    if (!item || item.adminOnly || (item.url && isAdminOnly(item.url)))
      return null;
    if (!userPages.length) return null;

    const filteredChildren = Array.isArray(item.items)
      ? item.items.map(filterNavTree).filter(Boolean)
      : null;

    const hasItemAccess = item.url
      ? hasPageAccess(userRole, [], item.url, userPages, !!user.fullAccess)
      : false;
    const hasChildAccess = filteredChildren && filteredChildren.length > 0;

    if (!hasItemAccess && !hasChildAccess) return null;

    return {
      ...item,
      items: filteredChildren || undefined,
    };
  };

  const filtered = (items || []).map(filterNavTree).filter(Boolean);

  return dedupeByUrl(filtered);
};

// Backward-compat alias (some bundles may still reference this)
const filterMenuItemsByModule = filterMenuItemsByPageAccess;

// Base menu items - one per page, filtered by user.pages (profile-based)
const baseMenuItems = {
  productManagement: [
    { name: translate("UI.MODELS"), url: "/device-models", icon: FiPlus },
    { name: translate("UI.PRODUCTS"), url: "/products", icon: FiInbox },
    { name: "Montaj", url: "/assembly-products", icon: FiTrello },
    { name: "Paketleme", url: "/packaging-products", icon: FiTool },
    { name: "Stok", url: "/stock-products", icon: FiPackage },
    {
      name: "Arızalı Ürünler",
      url: "/defective-products",
      icon: FiAlertCircle,
    },
  ],
  salesManagement: [
    { name: "Teklifler", url: "/orders", icon: FiShoppingBag },
    { name: "Siparişler", url: "/device-sales", icon: FiShoppingCart },
    { name: "Sevkiyatlar", url: "/deliveries", icon: FiPackage },
    { name: translate("UI.COMPANIES"), url: "/companies", icon: FiHome },
    { name: "Onaylar", url: "/permission-approves", icon: FiShield },
  ],
  reportsManagement: [
    {
      name: "Verimlilik Raporları",
      icon: FiBarChart2,
      items: [
        {
          name: "Makine Raporu",
          url: "/reports/machine-report",
          icon: FiBarChart2,
        },
        {
          name: "Özet Rapor",
          url: "/reports/oee",
          icon: FiBarChart2,
        },
        {
          name: "Makine Bazlı Rapor",
          url: "/reports/oee/by-machine",
          icon: FiCpu,
        },
        { name: "TEEP Şelalesi", url: "/teep", icon: FiBarChart2 },
      ],
    },
    {
      name: "Üretim Raporları",
      icon: FiTrendingUp,
      items: [
        {
          name: "Makine Bazlı",
          icon: FiCpu,
          items: [
            {
              name: "Üretim Çizgi Grafiği",
              url: "/reports/production/by-machine/trend",
              icon: FiTrendingUp,
            },
            {
              name: "Günlük Üretim Tablosu",
              url: "/reports/production/by-machine/distribution",
              icon: FiBarChart2,
            },
            {
              name: "Üretim Özeti",
              url: "/reports/production/by-machine/totals",
              icon: FiCpu,
            },
          ],
        },
        {
          name: "Parça Bazlı",
          icon: FiLayers,
          items: [
            {
              name: "Üretim Özeti",
              url: "/reports/production/by-part/per-part",
              icon: FiTrendingUp,
            },
            {
              name: "Üretim Çizgi Grafiği",
              url: "/reports/production/by-part/trend",
              icon: FiBarChart2,
            },
            {
              name: "Üretim Matrisi",
              url: "/reports/production/by-part/matrix",
              icon: FiLayers,
            },
          ],
        },
        {
          name: "Üretim Kapasitesi",
          url: "/production-capacity",
          icon: FiBarChart2,
        },
      ],
    },
    {
      name: "Duruş Raporları",
      icon: FiAlertCircle,
      items: [
        {
          name: "Özet Raporu",
          url: "/reports/downtime/trend",
          icon: FiTrendingUp,
        },
        {
          name: "Duruş Sebep Raporu",
          url: "/reports/downtime/reasons",
          icon: FiAlertCircle,
        },
        {
          name: "Makine Duruş Raporu",
          url: "/reports/downtime/by-machine",
          icon: FiCpu,
        },
      ],
    },
    {
      name: "Hurda Raporları",
      icon: FiCheckSquare,
      items: [
        {
          name: "Özet Raporu",
          url: "/reports/quality/trend",
          icon: FiTrendingUp,
        },
        {
          name: "Parça / Hurda Raporu",
          url: "/reports/quality/by-part",
          icon: FiPieChart,
        },
      ],
    },

    {
      name: "Ürün Analizi",
      url: "/reports/device-status",
      icon: FiPackage,
    },
    {
      name: "Sipariş Raporları",
      icon: FiShoppingCart,
      items: [
        {
          name: "Sipariş Trendi",
          url: "/reports/sales/trend",
          icon: FiTrendingUp,
        },
        {
          name: "Model Bazlı Sipariş",
          url: "/reports/sales/by-model",
          icon: FiPieChart,
        },
        {
          name: "Gelir Analizi",
          url: "/reports/sales/revenue",
          icon: FiBarChart2,
        },
      ],
    },
    {
      name: "Teslimat Raporları",
      icon: FiPackage,
      items: [
        {
          name: "Aylık Teslimat",
          url: "/reports/deliveries/trend",
          icon: FiTrendingUp,
        },
        {
          name: "Model Bazlı Teslimat",
          url: "/reports/deliveries/by-model",
          icon: FiPieChart,
        },
      ],
    },
    {
      name: "Operatör Aktiviteleri",
      url: "/reports/activities",
      icon: FiActivity,
    },
  ],
  productionManagement: [
    {
      name: "Dashboard",
      icon: FiBarChart2,
      items: [
        {
          name: "EPC",
          url: "/epc",
          icon: FiDatabase,
        },
        {
          name: "Dashboard",
          url: "/machine-monitor",
          icon: FiBarChart2,
        },
        {
          name: "Aktiviteler",
          url: "/activities",
          icon: FiActivity,
        },
      ],
    },
    {
      name: "Makineler",
      icon: FiCpu,
      items: [
        { name: "Cihazlar", url: "/devices", icon: FiHardDrive },
        { name: "Makineler", url: "/machines", icon: FiCpu },
        {
          name: "Makine Atamaları",
          url: "/machine-assignments",
          icon: FiServer,
        },
      ],
    },

    {
      name: "Parça Yönetimi",
      icon: FiList,
      items: [
        { name: "Parçalar", url: "/parts", icon: FiList },
        { name: "Parça Varyantları", url: "/part-variants", icon: FiList },
        { name: "Parça Birimleri", url: "/part-units", icon: FiList },
        { name: "Parça / Makine", url: "/part-machines", icon: FiList },
        { name: "Parça / Montaj", url: "/part-assemblies", icon: FiLayers },
        { name: "Ürün Ağacı", url: "/inventory-tree", icon: FiLayers },
      ],
    },
    {
      name: "Envanter Yönetimi",
      icon: FiPackage,
      items: [
        { name: "Envanter Listesi", url: "/inventory", icon: FiPackage },
        {
          name: "Envanter Girişleri",
          url: "/inventory/additions",
          icon: FiPackage,
        },
      ],
    },
    {
      name: "Üretim Planlama",
      icon: FiCalendar,
      items: [
        {
          name: "Üretim Planları",
          url: "/production-planning",
          icon: FiList,
        },
        // {
        //   name: "Üretilebilirlik Analizi",
        //   url: "/production-planning/feasibility",
        //   icon: FiBarChart2,
        // },
        {
          name: "Eksik Malzeme Raporu",
          url: "/production-planning/shortage",
          icon: FiAlertCircle,
        },
      ],
    },
    // {
    //   name: "Üretim Hatları",
    //   icon: FiGitPullRequest,
    //   items: [
    //     {
    //       name: "Üretim Hatları",
    //       url: "/production-lines",
    //       icon: FiGitPullRequest,
    //     },
    //     {
    //       name: "Alt Üretim Hatları",
    //       url: "/production-lines/sub",
    //       icon: FiGitPullRequest,
    //     },
    //     { name: "Üretim Geçmişi", url: "/production-logs", icon: FiFileText },
    //     {
    //       name: "Üretim Girişi",
    //       url: "/production-entry",
    //       icon: FiZap,
    //     },
    //   ],
    // },
    {
      name: "Duruş Yönetimi",
      icon: FiAlertCircle,
      items: [
        { name: "Vardiyalar", url: "/shifts", icon: FiClock },
        {
          name: "Duruş Grupları",
          url: "/stopping-groups",
          icon: FiAlertCircle,
        },
        {
          name: "Duruş Nedenleri",
          url: "/stop-reasons",
          icon: FiAlertCircle,
        },
        {
          name: "Duruş Kayıtları",
          url: "/machine-stop-logs",
          icon: FiStopCircle,
        },
      ],
    },
    {
      name: "Hurda Yönetimi",
      icon: FiTrash2,
      items: [
        {
          name: "Hurda Girişi",
          url: "/scrap-entry",
          icon: FiCheckSquare,
        },
        {
          name: "Hurda Grupları",
          url: "/scrap-groups",
          icon: FiLayers,
        },
      ],
    },
    {
      name: "OEE Yönetimi",
      icon: FiPieChart,
      items: [
        { name: "OEE Grupları", url: "/oee-groups", icon: FiList },
        { name: "OEE Sınıfları", url: "/oee-classes", icon: FiList },
      ],
    },
  ],
  userManagement: [
    { name: "Kullanıcılar", url: "/users", icon: FiUserCheck },
    {
      name: "Kullanıcı Profilleri",
      url: "/user-profiles",
      icon: FiShield,
    },
    {
      name: "Onay Akışları",
      url: "/approval-workflows",
      icon: FiGitPullRequest,
    },
    {
      name: "Sistem Firmaları",
      url: "/system/companies",
      icon: FiLayers,
      adminOnly: true,
    },
    {
      name: "Lisans Yönetimi",
      url: "/system/licenses",
      icon: FiShield,
      adminOnly: true,
    },
    {
      name: "Uygulama Logları",
      url: "/logs",
      icon: FiFileText,
      adminOnly: true,
    },
  ],
};

// Get menu items filtered by user profile (user.pages)
const getMenuItemsByModule = (user) => {
  if (!user) {
    return {
      productManagement: [],
      salesManagement: [],
      productionManagement: [],
      reportsManagement: [],
      userManagement: [],
    };
  }

  return {
    productManagement: filterMenuItemsByPageAccess(
      baseMenuItems.productManagement,
      user,
    ),
    salesManagement: filterMenuItemsByPageAccess(
      baseMenuItems.salesManagement,
      user,
    ),
    productionManagement: filterMenuItemsByPageAccess(
      baseMenuItems.productionManagement,
      user,
    ),
    reportsManagement: filterMenuItemsByPageAccess(
      baseMenuItems.reportsManagement,
      user,
    ),
    userManagement: filterMenuItemsByPageAccess(
      baseMenuItems.userManagement,
      user,
    ),
  };
};

// Export menuItems organized by user type (for Navigation component compatibility)
// These show all items for each role - actual filtering happens in getMenuItems based on user's modules
export const menuItems = {
  admin: {
    productManagement: baseMenuItems.productManagement,
    salesManagement: baseMenuItems.salesManagement,
    productionManagement: baseMenuItems.productionManagement,
    reportsManagement: baseMenuItems.reportsManagement,
    userManagement: baseMenuItems.userManagement,
  },
  employee: {
    productManagement: baseMenuItems.productManagement,
    salesManagement: baseMenuItems.salesManagement,
    productionManagement: baseMenuItems.productionManagement,
    reportsManagement: baseMenuItems.reportsManagement,
    userManagement: baseMenuItems.userManagement.filter(
      (item) => item.url !== "/users",
    ),
  },
  user: {
    productManagement: baseMenuItems.productManagement,
    salesManagement: baseMenuItems.salesManagement,
    productionManagement: baseMenuItems.productionManagement,
    reportsManagement: baseMenuItems.reportsManagement,
    userManagement: baseMenuItems.userManagement.filter(
      (item) => item.url !== "/users",
    ),
  },
  company: {
    productManagement: baseMenuItems.productManagement,
    salesManagement: baseMenuItems.salesManagement,
    productionManagement: baseMenuItems.productionManagement,
    reportsManagement: baseMenuItems.reportsManagement,
    userManagement: baseMenuItems.userManagement,
  },
};

// Dynamic menu items function that filters based on actual user
export const getMenuItems = (user) => {
  const result = getMenuItemsByModule(user);
  return result;
};

const _nav = [
  {
    category: "Üretim",
    items: (user) => {
      const items = getMenuItemsByModule(user).productionManagement;
      return items;
    },
  },
  {
    category: "Ürünler",
    items: (user) => {
      const items = getMenuItemsByModule(user).productManagement;
      return items;
    },
  },
  {
    category: "Siparişler",
    items: (user) => {
      const items = getMenuItemsByModule(user).salesManagement;
      return items;
    },
  },
  {
    category: "Raporlar",
    items: (user) => {
      const items = getMenuItemsByModule(user).reportsManagement;
      return items;
    },
  },
  {
    category: "Yönetim",
    items: (user) => {
      if (!user) return [];
      const items = [...getMenuItemsByModule(user).userManagement];
      items.push({
        name: "Bildirimler",
        url: "/notifications",
        icon: FiBell,
      });
      if (isNotificationAdmin(user)) {
        items.push({
          name: "Bildirim Yönetimi",
          url: "/settings/notifications",
          icon: FiBell,
        });
      }
      return items;
    },
  },
];

const NAV_MODULE_CATEGORIES = {
  productManagement: "Ürünler",
  salesManagement: "Siparişler",
  productionManagement: "Üretim",
  reportsManagement: "Raporlar",
  userManagement: "Yönetim",
};

export const buildPathToNavModuleMap = () => {
  const map = {};

  const walk = (items, moduleName) => {
    (items || []).forEach((item) => {
      if (item.url) {
        map[item.url] = moduleName;
      }
      if (item.items?.length) {
        walk(item.items, item.name);
      }
    });
  };

  Object.entries(NAV_MODULE_CATEGORIES).forEach(([key, categoryName]) => {
    walk(baseMenuItems[key], categoryName);
  });

  walk([{ name: "Ana Sayfa", url: "/" }], "Genel");
  walk(
    [
      { name: "Bildirimler", url: "/notifications" },
      { name: "Bildirim Yönetimi", url: "/settings/notifications" },
    ],
    "Yönetim",
  );

  return map;
};

export default _nav;
